import React, { useRef, useEffect, useCallback } from 'react';

// ─── Sparkle Particle System ────────────────────────────────────────────────

function createParticle(width, height, fromBottom = false) {
  return {
    x: Math.random(),
    y: fromBottom ? 1.0 + Math.random() * 0.05 : Math.random(),
    r: Math.random() * 1.4 + 0.4,
    speed: Math.random() * 0.06 + 0.02,
    driftX: (Math.random() - 0.5) * 0.015,
    phase: Math.random() * Math.PI * 2,
  };
}

function drawSparkle(ctx, px, py, r, opacity) {
  if (opacity <= 0) return;

  // Core circle
  ctx?.beginPath();
  ctx?.arc(px, py, r * 0.6, 0, Math.PI * 2);
  const alpha = Math.round(opacity * 255);
  ctx.fillStyle = `rgba(255,255,255,${opacity})`;
  ctx?.fill();

  // Horizontal + vertical rays — pale blue
  const rayLen = r * 2.2;
  const rayStroke = r * 0.35;
  ctx.strokeStyle = `rgba(184,212,232,${opacity * 0.7})`;
  ctx.lineWidth = rayStroke;
  ctx.lineCap = 'round';

  ctx?.beginPath();
  ctx?.moveTo(px - rayLen, py);
  ctx?.lineTo(px + rayLen, py);
  ctx?.stroke();

  ctx?.beginPath();
  ctx?.moveTo(px, py - rayLen);
  ctx?.lineTo(px, py + rayLen);
  ctx?.stroke();

  // Diagonal rays — lighter pale blue
  const diagLen = rayLen * 0.55;
  const diagStroke = r * 0.2;
  ctx.strokeStyle = `rgba(208,232,245,${opacity * 0.35})`;
  ctx.lineWidth = diagStroke;

  const d = diagLen * Math.SQRT1_2;
  ctx?.beginPath();
  ctx?.moveTo(px - d, py - d);
  ctx?.lineTo(px + d, py + d);
  ctx?.stroke();

  ctx?.beginPath();
  ctx?.moveTo(px + d, py - d);
  ctx?.lineTo(px - d, py + d);
  ctx?.stroke();
}

// ─── Main Component ──────────────────────────────────────────────────────────

const LiquidGradientBackground = ({ children }) => {
  const canvasRef = useRef(null);
  const particlesRef = useRef([]);
  const animFrameRef = useRef(null);
  const lastTimeRef = useRef(null);
  const gradientTRef = useRef(0);
  const gradientDirRef = useRef(1); // 1 = forward, -1 = reverse (ping-pong)

  const PARTICLE_COUNT = 80;
  const GRADIENT_DURATION = 8000; // ms

  // Initialise particles
  useEffect(() => {
    particlesRef.current = Array.from({ length: PARTICLE_COUNT }, (_, i) =>
      createParticle(1, 1, false)
    );
  }, []);

  const draw = useCallback((timestamp) => {
    const canvas = canvasRef?.current;
    if (!canvas) return;
    const ctx = canvas?.getContext('2d');
    const W = canvas?.width;
    const H = canvas?.height;

    // Delta time
    if (lastTimeRef?.current === null) lastTimeRef.current = timestamp;
    const delta = Math.min((timestamp - lastTimeRef?.current) / 1000, 0.05); // seconds, capped
    lastTimeRef.current = timestamp;

    // Advance gradient t (ping-pong over GRADIENT_DURATION)
    gradientTRef.current += gradientDirRef?.current * (delta / (GRADIENT_DURATION / 1000));
    if (gradientTRef?.current >= 1) { gradientTRef.current = 1; gradientDirRef.current = -1; }
    if (gradientTRef?.current <= 0) { gradientTRef.current = 0; gradientDirRef.current = 1; }
    const t = gradientTRef?.current;

    // ── Clear ──
    ctx?.clearRect(0, 0, W, H);

    // ── Base fill: #0D1117 ──
    ctx.fillStyle = '#0D1117';
    ctx?.fillRect(0, 0, W, H);

    // ── Blob 1 — Grey-Blue (top area) ──
    {
      const cx = W * (0.15 + 0.35 * Math.sin(t * Math.PI * 2));
      const cy = H * (0.10 + 0.15 * Math.cos(t * Math.PI * 2));
      const rx = W * 1.1 / 2;
      const ry = H * 0.65 / 2;

      const grad = ctx?.createRadialGradient(cx, cy, 0, cx, cy, Math.max(rx, ry));
      grad?.addColorStop(0, `rgba(42,63,90,${184 / 255})`);
      grad?.addColorStop(0.5, `rgba(26,37,53,${97 / 255})`);
      grad?.addColorStop(1, 'rgba(0,0,0,0)');

      ctx?.save();
      ctx?.translate(cx, cy);
      ctx?.scale(rx / Math.max(rx, ry), ry / Math.max(rx, ry));
      ctx?.beginPath();
      ctx?.arc(0, 0, Math.max(rx, ry), 0, Math.PI * 2);
      ctx.fillStyle = grad;
      ctx?.fill();
      ctx?.restore();
    }

    // ── Blob 2 — Lighter Blue-Grey (bottom area) ──
    {
      const cx = W * (0.75 - 0.25 * Math.sin(t * Math.PI * 2 + 1.2));
      const cy = H * (0.72 + 0.12 * Math.cos(t * Math.PI * 2 + 0.8));
      const rx = W * 0.9 / 2;
      const ry = H * 0.55 / 2;

      const grad = ctx?.createRadialGradient(cx, cy, 0, cx, cy, Math.max(rx, ry));
      grad?.addColorStop(0, `rgba(58,80,104,${140 / 255})`);
      grad?.addColorStop(0.5, `rgba(30,45,61,${71 / 255})`);
      grad?.addColorStop(1, 'rgba(0,0,0,0)');

      ctx?.save();
      ctx?.translate(cx, cy);
      ctx?.scale(rx / Math.max(rx, ry), ry / Math.max(rx, ry));
      ctx?.beginPath();
      ctx?.arc(0, 0, Math.max(rx, ry), 0, Math.PI * 2);
      ctx.fillStyle = grad;
      ctx?.fill();
      ctx?.restore();
    }

    // ── Blob 3 — Static Depth Centre Mass ──
    {
      const cx = W * 0.5;
      const cy = H * 0.5;
      const rx = W * 0.7 / 2;
      const ry = H * 0.4 / 2;

      const grad = ctx?.createRadialGradient(cx, cy, 0, cx, cy, Math.max(rx, ry));
      grad?.addColorStop(0, `rgba(22,28,38,${153 / 255})`);
      grad?.addColorStop(1, 'rgba(0,0,0,0)');

      ctx?.save();
      ctx?.translate(cx, cy);
      ctx?.scale(rx / Math.max(rx, ry), ry / Math.max(rx, ry));
      ctx?.beginPath();
      ctx?.arc(0, 0, Math.max(rx, ry), 0, Math.PI * 2);
      ctx.fillStyle = grad;
      ctx?.fill();
      ctx?.restore();
    }

    // ── Sparkle Particles ──
    const particles = particlesRef?.current;
    for (let i = 0; i < particles?.length; i++) {
      const p = particles?.[i];

      // Advance phase: 2 full cycles/sec
      p.phase += delta * Math.PI * 4;

      // Move upward
      p.y -= p?.speed * delta;

      // Horizontal drift, clamped
      p.x = Math.max(0, Math.min(1, p?.x + p?.driftX * delta));

      // Respawn if off top
      if (p?.y < 0) {
        particles[i] = createParticle(W, H, true);
        continue;
      }

      // Twinkle opacity
      const sinVal = (Math.sin(p?.phase) + 1) / 2;
      let opacity = (0.3 + 0.7 * sinVal * sinVal) * 0.85;

      // Bottom fade-in zone (bottom 10%)
      if (p?.y > 0.9) opacity *= (1.0 - p?.y) / 0.10;

      // Top fade-out zone (top 10%)
      if (p?.y < 0.1) opacity *= p?.y / 0.10;

      opacity = Math.max(0, Math.min(1, opacity));

      drawSparkle(ctx, p?.x * W, p?.y * H, p?.r, opacity);
    }

    animFrameRef.current = requestAnimationFrame(draw);
  }, []);

  // Canvas resize + animation loop
  useEffect(() => {
    const canvas = canvasRef?.current;
    if (!canvas) return;

    const resize = () => {
      const parent = canvas?.parentElement;
      canvas.width = parent?.offsetWidth || window.innerWidth;
      canvas.height = parent?.offsetHeight || window.innerHeight;
    };

    resize();
    window.addEventListener('resize', resize);
    animFrameRef.current = requestAnimationFrame(draw);

    return () => {
      window.removeEventListener('resize', resize);
      if (animFrameRef?.current) cancelAnimationFrame(animFrameRef?.current);
    };
  }, [draw]);

  return (
    <div className="absolute inset-0 w-full h-full overflow-hidden">
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full"
        style={{ display: 'block' }}
      />
      {children}
    </div>
  );
};

export default LiquidGradientBackground;
